import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  SvgIcon,
  useTheme,
  Stack,
  Tooltip,
  alpha,
} from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { getRegularDate } from "utils/time.util";
import {
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";

import { ReactComponent as summarizerLogo } from "assets/icons/summarizer-icon.svg";
import { ReactComponent as citationLogo } from "assets/icons/citation-generator-icon.svg";
import { ReactComponent as readingLogo } from "assets/icons/reading-level-icon.svg";

import { useDispatch, useSelector } from "react-redux";
import { writeStorage, useLocalStorage } from "@rehooks/local-storage";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { GppMaybeOutlined } from "@mui/icons-material";
import ColumnsSection from "./ColumnSection";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";

export default function RecentDataGrid({ recentCollectionSource }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const defaultVisibilityModel = {
    createdDateTime_dtm: false,
    title_chr: true,
    sourceName_chr: true,
    sourcePublishedDateTime_dtm: true,
    score: false,
    mediaBiasRating: false,
    charityNavigatorSource: false,
    isSummaryAvailable_ysn: false,
    isCitationAvailable_ysn: true,
    isReadingLevelAvailable_ysn: false,
    notesCount_cnt: true,
  };

  const { user } = useSelector((state) => state.auth);

  const [visibilityModel, setVisibilityModel] = useLocalStorage(
    `columnvisibility-${user.userId}`,
    defaultVisibilityModel
  );

  const [persistedWidths, setPersistedWidths] = useLocalStorage(
    `columnWidths-${user.userId}`,
    {}
  );

  const [commonWidth, setCommonWidth] = useState(null);

  const columnWidthsRef = useRef(persistedWidths);
  const containerRef = useRef(null);

  const computeCommonWidth = () => {
    const element = containerRef.current;
    let width = element ? element.clientWidth : 0;

    const visibleFields = Object.keys(visibilityModel).filter(
      (field) => visibilityModel[field]
    );
    const unsetCount = visibleFields.length;
    const newCommonWidth = unsetCount > 0 ? Math.floor(width / unsetCount) : 0;
    setCommonWidth(newCommonWidth);
  };

  const debouncedUpdateColumnWidths = () => {
    writeStorage(`columnWidths-${user.userId}`, columnWidthsRef.current);
    setPersistedWidths(columnWidthsRef.current);
    computeCommonWidth();
  };

  const handleColumnResize = (params) => {
    columnWidthsRef.current = {
      ...columnWidthsRef.current,
      [params.colDef.field]: params.width,
    };
    debouncedUpdateColumnWidths();
  };

  useEffect(() => {
    if (!visibilityModel || Object.keys(visibilityModel).length === 0) {
      setVisibilityModel(defaultVisibilityModel);
      writeStorage(`columnvisibility-${user.userId}`, defaultVisibilityModel);
      apiRef.current.setColumnVisibilityModel(defaultVisibilityModel);
    } else {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  }, []);

  const handleVisibilityChange = (newVisibilityModel) => {
    const updatedVisibility = {
      ...visibilityModel,
      ...newVisibilityModel,
    };
    setVisibilityModel(updatedVisibility);
    writeStorage(`columnvisibility-${user.userId}`, updatedVisibility);
  };

  const handleToggleColumnVisibility = (event, field) => {
    const newVisibilityModel = {
      ...visibilityModel,
      [field]: event.target.checked,
    };
    setVisibilityModel(newVisibilityModel);
    writeStorage(`columnvisibility-${user.userId}`, newVisibilityModel);
  };

  const handleSidebarOpen = (item, option) => {
    dispatch(setCollectionSourceId(item.collectionSourceID_ids));
    dispatch(setLevel(""));
    dispatch(setSelectedBookmarkOption(option));
    dispatch(setReadingLevelAvailable(item.isReadingLevelAvailable_ysn));
    dispatch(
      setSummaryInfoData({
        publisher: item.sourceName_chr,
        title: item.title_chr,
        published_date: item.sourcePublishedDateTime_dtm,
        url: item.sourceUrl_chr,
        utc_date_published: item.sourcePublishedDateTime_dtm
      })
    );
    dispatch(setCollectionId(item.collectionID_lng));
    dispatch(setIsCitationAvailable(item.isCitationAvailable_ysn));
    dispatch(setIsSummaryAvailable(item.isSummaryAvailable_ysn));
    dispatch(setIsSidebarOpen(true));
  };

  const fields = [
    "createdDateTime_dtm",
    "title_chr",
    "sourceName_chr",
    "sourcePublishedDateTime_dtm",
    "score",
    "mediaBiasRating",
    "charityNavigatorSource",
    "isSummaryAvailable_ysn",
    "notesCount_cnt",
    "isCitationAvailable_ysn",
    "isReadingLevelAvailable_ysn",
  ];
  
    const columns = useColumns({
      fields,
      persistedWidths,
      commonWidth,
      theme,
      handlers: { handleSidebarOpen },
    });

  const scrollToLeft = () => {
    const virtualScroller = document.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (virtualScroller) {
      virtualScroller.scrollLeft = 0;
    }
  };
  const clearWidthModel = () => {
    scrollToLeft();
    writeStorage(`columnWidths-${user.userId}`, {});
    setCommonWidth(null);
    setPersistedWidths({});
    columnWidthsRef.current = {};
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={columns}
        label={`columnvisibility-${user.userId}`}
        clearWidthModel={clearWidthModel}
        widthModal={persistedWidths}
      />
       <CollectionTableComponent
          apiRef={apiRef}
          rows={recentCollectionSource}
          columns={columns}
          handleColumnResize={handleColumnResize}
          visibilityModel={visibilityModel}
          handleVisibilityChange={handleVisibilityChange}
          getRowId={(row) => row.collectionSourceID_ids}
          getRowClassName={({ row }) =>
            row.isMisinformationSource_ysn ? "sooth-misinformation-row" : ""
          }
        />
    </Box>
  );
}
